import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../utils/api';
import { useToast } from '../components/Toast';
import { useTableControls } from '../hooks/useTableControls';
import SearchBox from '../components/SearchBox';
import StatusTabs from '../components/StatusTabs';
import SortableTh from '../components/SortableTh';
import Pagination from '../components/Pagination';
import Icon from '../components/Icon';

const STATUS_TABS = [
  { value: 'all', label: 'All messages' },
  { value: 'replied', label: 'Replied' },
  { value: 'escalated', label: 'Escalated' },
  { value: 'lead', label: 'Lead' },
  { value: 'pending', label: 'Pending' },
];

function initials(name) {
  return (name || '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(s => s[0].toUpperCase())
    .join('') || '?';
}

export default function MessagesPage() {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = useCallback(() =>
    api.getMessages()
      .then(r => { setItems(r.items || []); setLastUpdated(new Date()); })
      .catch(() => toast('Failed to load messages', 'error'))
      .finally(() => setLoading(false)), [toast]);

  useEffect(() => {
    load();
    const t = setInterval(() => load(), 30000);
    return () => clearInterval(t);
  }, [load]);

  const handleFetchNow = async () => {
    setFetching(true);
    try {
      await api.fetchMessages();
      toast('Fetch triggered — refreshing in 5 seconds…', 'success');
      setTimeout(() => load(), 5000);
    } catch {
      toast('Failed to trigger message fetch', 'error');
    } finally {
      setFetching(false);
    }
  };

  const tc = useTableControls({
    items,
    searchFields: (m) => [m.sender_name, m.content, m.classification, m.reply_content],
    statusOf: (m) => m.status,
    sorters: {
      created_at: (m) => m.created_at || '',
      sender_name: (m) => m.sender_name || '',
      status: (m) => m.status || '',
    },
    defaultSort: 'created_at',
    defaultDir: 'desc',
    pageSize: 10,
  });

  return (
    <>
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h1>Inbox Messages</h1>
        <button
          className="btn btn-primary"
          onClick={handleFetchNow}
          disabled={fetching}
          style={{ minWidth: 130 }}
        >
          {fetching ? 'Fetching…' : 'Fetch Now'}
        </button>
      </div>

      {!loading && items.length > 0 && (
        <section className="summary-bar">
          <div className="summary-item">
            <span className="summary-value">{items.length}</span>
            <span className="summary-label">Total messages</span>
          </div>
          <div className="summary-divider" />
          <div className="summary-item attention">
            <span className="summary-value">{tc.counts.pending || 0}</span>
            <span className="summary-label">Pending</span>
          </div>
          <div className="summary-divider" />
          <div className="summary-item danger">
            <span className="summary-value">{tc.counts.escalated || 0}</span>
            <span className="summary-label">Escalated</span>
          </div>
          <div className="summary-divider" />
          <div className="summary-item success">
            <span className="summary-value">{tc.counts.replied || 0}</span>
            <span className="summary-label">Replied</span>
          </div>
          {lastUpdated && (
            <div className="summary-updated">
              <span className="live-dot" />
              Updated {lastUpdated.toLocaleTimeString()}
            </div>
          )}
        </section>
      )}

      <section className="list-panel">
        <div className="panel-toolbar">
          <div className="panel-toolbar-title">
            <h2>All messages</h2>
            <span>{tc.sorted.length} records</span>
          </div>
          <div className="panel-toolbar-actions">
            <SearchBox value={tc.search} onChange={tc.setSearch} placeholder="Search messages…" />
            <button type="button" className="icon-btn" onClick={load} title="Refresh">
              <Icon name="refresh" />
            </button>
          </div>
        </div>

        <StatusTabs tabs={STATUS_TABS} active={tc.activeStatus} onChange={tc.setActiveStatus} counts={tc.counts} total={items.length} />

        <div className="table-wrap">
          {loading ? (
            <div className="empty-state"><p>Loading…</p></div>
          ) : items.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">✉️</div>
              <p>No messages found</p>
            </div>
          ) : tc.paginated.length === 0 ? (
            <div className="empty-state"><p>No messages match your search or filters.</p></div>
          ) : (
            <table>
              <thead>
                <tr>
                  <SortableTh col="created_at" label="Date" sortCol={tc.sortCol} sortDir={tc.sortDir} onSort={tc.handleSort} />
                  <SortableTh col="sender_name" label="From" sortCol={tc.sortCol} sortDir={tc.sortDir} onSort={tc.handleSort} />
                  <th>Message</th>
                  <th>Classification</th>
                  <SortableTh col="status" label="Status" sortCol={tc.sortCol} sortDir={tc.sortDir} onSort={tc.handleSort} />
                  <th>Reply sent</th>
                </tr>
              </thead>
              <tbody>
                {tc.paginated.map(m => (
                  <tr key={m.message_id}>
                    <td className="td-mono">{m.created_at?.slice(0, 10)}</td>
                    <td>
                      <span className="name-cell">
                        <span className="name-avatar">{initials(m.sender_name)}</span>
                        {m.sender_name}
                      </span>
                    </td>
                    <td className="td-truncate" style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>{m.content}</td>
                    <td><span className="badge badge-draft" style={{ fontSize: '0.72rem' }}>{m.classification}</span></td>
                    <td><span className={`badge badge-${m.status} badge-dot`}>{m.status}</span></td>
                    <td className="td-truncate" style={{ fontSize: '0.82rem', color: 'var(--teal)' }}>
                      {m.reply_content || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <Pagination
          pageSize={tc.pageSize}
          setPageSize={tc.setPageSize}
          currentPage={tc.currentPage}
          setCurrentPage={tc.setCurrentPage}
          totalPages={tc.totalPages}
          pageStart={tc.pageStart}
          pageEnd={tc.pageEnd}
          total={tc.sorted.length}
          paginationItems={tc.paginationItems}
        />
      </section>
    </>
  );
}
